import { ElementType, ReactNode } from 'react'
import Typography, { TypographyProps } from '@mui/material/Typography'
import { Casing, TEXT_ROLE_CASING, TextRole } from '@common/helpers/casing'
import cn from 'classnames'
import styles from './Text.module.scss'
import { formatChildren } from './formatChildren'

export type TextTone =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'disabled'
  | 'link'
  | 'inverse'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'positive'
  | 'negative'

const DEFAULT_ELEMENT: Record<TextRole, ElementType> = {
  hero: 'h1',
  display: 'p',
  metric: 'span',
  pageTitle: 'h1',
  cardTitle: 'h2',
  modalTitle: 'h2',
  sectionTitle: 'h3',
  title: 'h3',
  tab: 'span',
  columnHeader: 'span',
  fieldLabel: 'span',
  eyebrow: 'span',
  button: 'span',
  menuItem: 'span',
  bodyLg: 'p',
  body: 'p',
  bodySm: 'p',
  caption: 'span',
  pill: 'span',
  amount: 'span',
  label: 'span',
}

export interface ITextProps extends Omit<TypographyProps, 'variant' | 'children' | 'color'> {
  /** The role the text plays: it sets the type style, the default element and the case. */
  variant?: TextRole
  /**
   * Overrides the role's case. `none` for data that must show exactly as stored; `role` marks a
   * variable child as copy, keeping the role's case. ESLint requires one when the only child is a
   * variable.
   */
  casing?: Casing | 'role'
  /**
   * The text's color, by meaning. Unset inherits, so text on a filled control keeps its color.
   * `positive`/`negative` are money moving up or down; `success`/`error` are statuses.
   */
  tone?: TextTone
  /** Emphasis inside a body role: weight 600. Titles are already 600. */
  strong?: boolean
  component?: ElementType
  /** For `component="label"`: the control the label names. */
  htmlFor?: string
  children?: ReactNode
}

/**
 * Every piece of copy in Atlas renders through this: its role picks the type style (the
 * `--type-*` tokens) and the capitalization (`common/helpers/casing.ts`). Built on MUI
 * Typography, so `component`, `noWrap` and `gutterBottom` keep working.
 */
const Text = ({
  variant = 'body',
  casing,
  tone,
  strong,
  component,
  className,
  children,
  ...props
}: ITextProps) => (
  <Typography
    {...props}
    variant="inherit"
    component={component ?? DEFAULT_ELEMENT[variant]}
    className={cn(
      styles[variant],
      tone && styles[`tone-${tone}`],
      strong && styles.strong,
      className,
    )}
  >
    {formatChildren(!casing || casing === 'role' ? TEXT_ROLE_CASING[variant] : casing, children)}
  </Typography>
)

export default Text
